import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { introSlider } from './data';
import { ApiService } from '../../shared/services/api.service';
import { ModalService } from '../../shared/services/modal.service';
import { CreditPack, CreditPackTool, User, isLargePack } from '../../shared/classes/app.models';
import { tierCopy } from '../../shared/classes/plan-copy';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { ToastrService } from 'ngx-toastr';

/** A pack's tool lines, bucketed by the group_key the backend derives. */
export interface PackToolGroup {
  key: string;
  title: string;
  /** The line for the INCLUDES list, e.g. "8 editing tools". */
  feature: string;
  tools: CreditPackTool[];
}

/**
 * A pack with everything the card shows worked out up front.
 *
 * The card needs prices, group counts and label text that the API does not send;
 * computing them once here rather than through template getters keeps the arrays
 * stable between change detection runs, which OnPush relies on.
 */
export interface PackView extends CreditPack {
  /** The charge for the whole term — what the customer actually pays. */
  price: number;
  /** The headline figure: what the term works out to a month. */
  perMonth: number;
  /** Normally 'month'; a term of unknown length keeps quoting its own period. */
  headlinePeriod: string;
  /** The term charge, set only where it is not simply perMonth again. */
  termTotal: number | null;
  /** The tier's one-line description, under the name. */
  lead: string;
  /** The heading over the bullets — "Includes", or "Everything in Starter, and". */
  includesLead: string;
  features: string[];
  info: string[];
  /** What the plan will hold: "200 credits", "300 images", "500 credits + 300 images". */
  grantLine: string;
  groups: PackToolGroup[];
  toolCount: number;
  isActivated: boolean;
}

/** Durations the backend allows, longest last — the order the pills appear in. */
const DURATIONS: { [key: string]: { label: string; period: string; months: number; span: string } } = {
  monthly:       { label: 'Monthly',     period: 'month',    months: 1,  span: 'a month' },
  quarterly:     { label: 'Quarterly',   period: 'quarter',  months: 3,  span: 'three months' },
  'half-yearly': { label: 'Half-yearly', period: '6 months', months: 6,  span: 'six months' },
  yearly:        { label: 'Yearly',      period: 'year',     months: 12, span: 'a year' },
};
const DURATION_ORDER = ['monthly', 'quarterly', 'half-yearly', 'yearly'];

/**
 * How a group of tools is described. `one`/`many` are counted nouns; a group
 * with an empty pair is named outright however many lines it has, because a
 * count reads as nonsense there ("1 chat tool").
 */
const TOOL_GROUPS: { [key: string]: { title: string; one: string; many: string; fixed?: string } } = {
  image:   { title: 'AI image models', one: 'AI image model', many: 'AI image models' },
  video:   { title: 'AI video models', one: 'AI video model', many: 'AI video models' },
  edit:    { title: 'Editing tools',   one: 'editing tool',   many: 'editing tools' },
  upscale: { title: 'Upscaling',       one: 'upscale option', many: 'upscale options' },
  chat:    { title: 'Chat',            one: '', many: '', fixed: 'AI chat assistant' },
  stock:   { title: 'Stock library',   one: '', many: '', fixed: 'Stock library downloads' },
};
const GROUP_ORDER = ['image', 'video', 'edit', 'upscale', 'chat', 'stock'];

@Component({
  selector: 'plan-page',
  templateUrl: './plan.component.html',
  styleUrls: ['./plan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PlanComponent implements OnInit {
introSlider = introSlider;
  loaded = false;
  setting: any;
  slides = [];
  isMobile = false;

  /** Every pack on offer, cheapest first, split by the tab that shows it. */
  public imagePacks: PackView[] = [];
  public videoPacks: PackView[] = [];

  /** The active tab narrowed to the selected duration — what the grid renders. */
  public visiblePacks: PackView[] = [];
  public durations: string[] = [];
  public selectedDuration = '';
  public popularPackId: number | null = null;

  public currentUser: User;

  activePlanId: number | null = null;

  @ViewChild('form', {static: false}) form: ElementRef;

  /** The horizontal pack rail, so the arrows have something to scroll. */
  @ViewChild('packTrack', {static: false}) packTrack: ElementRef<HTMLElement>;

  /**
   * Whether each arrow has anywhere to go.
   *
   * Both false when the cards already fit, which is also what hides the arrows:
   * a control that cannot do anything should not be sitting over the cards.
   */
  canScrollPacksLeft = false;
  canScrollPacksRight = false;

  customPlanForm: FormGroup;
  faqOpen: number | null = null;

  /**
   * The questions, from the pricing page's own FAQ block.
   *
   * Data rather than markup because the answers are not all one paragraph: some
   * carry bullet lists, and writing each out by hand meant six near-identical
   * blocks whose indices had to be kept in step with toggleFaq() by counting.
   *
   * An answer is a list of blocks: a string is a paragraph, `{ list: [...] }` is
   * a bullet list — the same shape the pricing page uses, so the two stay
   * comparable when either is edited.
   *
   * KEEP THE FIGURES IN STEP with the packs. These state credit costs, and the
   * authority for those is oxzin_indiapicture_exclusive_credit_pack_detail; the
   * cards above read from it, this text does not.
   */
  readonly faqs: { q: string; a: (string | { list: string[] })[] }[] = [
    {
      q: 'How does the credit system work?',
      a: [
        'Your credits are used across supported IndiaPicture creation tools. Credit consumption depends on the type of generation or action you choose.',
        'For the Starter subscription:',
        { list: ['1 credit per image generation', '2 credits per video generation', '2 credits per stock download', '4 credits per stock video'] },
        'Credit usage can vary by the selected product, tool and generation type. Quarterly plans receive 5% off and yearly plans receive 20% off, which reduces the effective cost per credit further.'
      ]
    },
    {
      q: 'What is the difference between Monthly, Quarterly and Yearly plans?',
      a: [
        'All three provide the same core subscription benefits — the billing commitment and pricing differ.',
        { list: ['Monthly: pay month-to-month, cancel anytime.', 'Quarterly: pay upfront for 3 months and save 5%.', 'Yearly: pay upfront for 12 months and save 20%.'] }
      ]
    },
    {
      q: 'What is the difference between Standard and Enhanced packs?',
      a: [
        'Standard is designed for regular image or video generation at a lower pack price. Enhanced is the premium generation tier at a higher price. The 25-image Enhanced pack also includes Professional Upscaling.',
        'Each pack includes a fixed number of credits for its image generations and prompts. Standard image packs include 2, 2 and 8 credits for the 2-, 5- and 25-image packs; Enhanced image packs include 2, 3 and 13 credits respectively.'
      ]
    },
    {
      q: 'How many credits does video generation use?',
      a: [
        'In the Starter subscription, video generation uses 2 credits per video and stock video uses 4 credits per video.',
        'One-time Video Packs include a fixed number of credits for their video generations and prompts:',
        { list: ['1 Video: 5 credits included', '3 Videos: 8 credits included', '12 Videos: 16 credits included'] },
        'Video Pack pricing and these credit values are defined separately from subscription credit rates.'
      ]
    },
    {
      q: 'Which AI models and tools are available?',
      a: [
        'The Starter plan includes access to the following:',
        {
          list: [
            'Image models: imagen-4, gpt-image-1, gpt-image-2, nano-banana-pro, nano-banana',
            'Video models: Seedance-1-Pro, HappyHorse-1.0, Wan-2.2-Fast, Wan-2.5',
            'Upscaling models: Real-ESRGAN, Bria'
          ]
        }
      ]
    },
    {
      q: 'What can I use my credits for?',
      a: [
        'Depending on your plan and available tools, credits can be used across image generation, video generation, editing tools, stock downloads, upscaling and other supported creation tools. Credit usage depends on the selected generation type or tool.'
      ]
    },
    {
      q: 'Do Image or Video Packs require a subscription?',
      a: [
        'No. Packs are designed for one-time usage, so you can purchase an Image Pack or Video Pack without subscribing to a monthly, quarterly or yearly plan.'
      ]
    },
    {
      q: 'Can I get a custom plan for my team?',
      a: [
        'Yes. The Custom plan is designed for larger teams that need:',
        { list: ['Custom credit allocation', 'Shared / team credit pools', 'Multiple users', 'Custom commercial terms', 'Invoice billing', 'Dedicated sales support', 'Exclusive Stock Access'] },
        'Get in touch and we will build a plan around your workflow.'
      ]
    }
  ];

  /**
   * The Custom card, last in the plan row.
   *
   * Not a pack and deliberately not shaped like one: no price, no credit count
   * and no checkout, because there is nothing to buy until sales has scoped it.
   * It sits with the tiers rather than only in the enquiry section below so the
   * reader meets it while they are still deciding, not after they have given up.
   */
  readonly customPlan = {
    eyebrow: 'Built around your workflow.',
    name: 'Custom',
    lead: 'For larger teams with unique volume, collaboration or commercial requirements.',
    pillars: ['Custom credits', 'Custom users', 'Custom billing'],
    pitch: 'Need more volume, shared credit pools, multiple users or a tailored '
         + 'commercial setup? Talk to our team.',
    includesLead: 'Need more capacity or a tailored setup?',
    includes: [
      'Custom credit allocation',
      'Shared / team credit pools',
      'Multiple users',
      'Custom commercial terms',
      'Invoice billing',
      'Dedicated sales support',
      'Exclusive Stock Access'
    ],
    bestFor: 'Enterprise & multi-team organisations'
  };

  /**
   * How far a term's credits go, tool by tool.
   *
   * `per` is the credit cost of one use; the counts in the table are derived from
   * it and the plan's own credits, never listed — a written count would disagree
   * with the card above it the moment either number moved.
   *
   * The costs themselves are copy, like the model tables: credit_pack_detail is
   * what actually charges.
   */
  readonly compareGroups: { title: string; rows: { tool: string; per: number; sub?: string }[] }[] = [
    {
      title: 'Image AI models',
      rows: [
        { tool: 'Imagen 4',        per: 1 },
        { tool: 'GPT Image 1',     per: 1 },
        { tool: 'GPT Image 2',     per: 1 },
        { tool: 'Nano Banana Pro', per: 2 },
        { tool: 'Nano Banana',     per: 1 }
      ]
    },
    {
      title: 'Video AI models',
      rows: [
        { tool: 'Seedance 1 Pro', per: 4 },
        { tool: 'HappyHorse 1.0', per: 4 },
        { tool: 'Wan 2.2 Fast',   per: 6 },
        { tool: 'Wan 2.5',        per: 18 }
      ]
    },
    {
      title: 'Editing',
      rows: [
        {
          tool: 'Editing tools',
          per: 1,
          sub: 'Colorize, Denoise, Erase, Face Restore, Fill, Remove Background, Replace Background, Restore'
        }
      ]
    }
  ];

  readonly compareNote =
    'Counts assume a whole term’s credits go to one tool — in practice you’ll mix tools, so your '
    + 'totals will vary. Quarterly and yearly plans pool credits across the term.';

  /**
   * The plan columns, built from the packs on sale for the selected term.
   *
   * Read from the packs rather than written down, so the header can never quote a
   * price the card beside it does not. Rebuilt whenever the term changes.
   */
  compareColumns: { name: string; price: number; credits: number; featured: boolean }[] = [];

  /**
   * Which column the narrow layout shows. Three plan columns plus the tool name
   * do not fit a phone, so below 760px one is picked at a time.
   */
  comparePick = '';

  /**
   * Which half of the AI pricing table is showing.
   *
   * Its own field, not activeTab: that one picks which PACKS are on sale and is
   * hidden when only one kind exists. This picks which MODELS are listed, and
   * both kinds are always listed whatever is on sale.
   */
  modelTab: 'images' | 'videos' = 'images';

  /**
   * What one generation costs, per model.
   *
   * Copy, not derived from the packs, and the distinction matters: these are the
   * published rates for the catalogue, where credit_pack_detail holds what each
   * PACK charges. They agree today and nothing makes them agree tomorrow — if a
   * pack is repriced, this table needs the same edit.
   *
   * Resolutions and durations are listed on the model's own row rather than split
   * into one row each, because within a model they do not change the cost.
   */
  readonly imageModels: { model: string; output: string; res: string[]; credits: number }[] = [
    { model: 'Imagen 4',        output: 'Still Image', res: ['1K', '2K'],       credits: 1 },
    { model: 'GPT Image 1',     output: 'Still Image', res: ['1K'],             credits: 1 },
    { model: 'GPT Image 2',     output: 'Still Image', res: ['1K'],             credits: 1 },
    { model: 'Nano Banana Pro', output: 'Still Image', res: ['1K', '2K', '4K'], credits: 1 },
    { model: 'Nano Banana',     output: 'Still Image', res: ['1K'],             credits: 1 }
  ];

  readonly imageModelsNote =
    'Credits are charged per generation. Resolution does not change the credit cost for these models.';

  readonly videoModels: { model: string; durations: string[]; res: string[]; credits: number }[] = [
    { model: 'Seedance 1 Pro', durations: ['5s', '10s'],                      res: ['480p', '720p', '1080p'], credits: 8 },
    { model: 'HappyHorse 1.0', durations: ['3s', '5s', '8s', '10s', '15s'],   res: ['720p', '1080p'],         credits: 2 },
    { model: 'Wan 2.2 Fast',   durations: ['5s', '7s'],                       res: ['480p', '720p'],          credits: 2 },
    { model: 'Wan 2.5',        durations: ['5s', '10s'],                      res: ['480p', '720p', '1080p'], credits: 3 }
  ];

  readonly videoModelsNote =
    'Credits are charged per generation. Duration and resolution do not change the credit cost within a model.';

  /**
   * What every tier carries, for the strip below the plans.
   *
   * Copy, not derived: these are categories of capability ("AI Generation"),
   * where the pack rows hold individual service codes. Deriving them would mean
   * mapping 27 tools onto six words and getting a different six the moment a
   * tool was added.
   */
  readonly planIncludes: string[] = [
    'AI Generation',
    'Editing',
    'Video Generation',
    'Stock Library',
    'Standard Licence',
    'Full-resolution Downloads'
  ];

  /**
   * What a credit buys per action — the panel behind the "Credit usage" marker.
   *
   * Written down rather than derived: the pack rows price 27 individual tools,
   * and "Generation · Video" is not one of them but four models charging 2, 2, 3
   * and 8. A derived figure would be a range, which is what this panel exists not
   * to be; the per-model table is where that detail belongs.
   *
   * KEEP IN STEP with credit_pack_detail. These are the Starter rates, and
   * nothing recomputes them if the packs are repriced.
   */
  readonly creditUsage: { label: string; image: number; video:any}[] = [
    { label: 'Generation', image: 1, video: 2 },
    { label: 'Stock',      image: 2, video: 'Coming Soon' }
  ];

  /** Whether an answer block is a bullet list rather than a paragraph. */
  public isFaqList(block: string | { list: string[] }): boolean {
    return typeof block !== 'string';
  }

  /** The bullets, for a block isFaqList() has already accepted. */
  public faqListItems(block: string | { list: string[] }): string[] {
    return typeof block === 'string' ? [] : block.list;
  }
  enquirySubmitting = false;
  enquirySuccess = false;
  enquiryError = '';

  constructor(
    public apiService: ApiService,
    private modalService: ModalService,
    private cdref: ChangeDetectorRef,
    private router: Router,
    private authenticationService: AuthenticationService,
    private fb: FormBuilder,
    private toastrService: ToastrService
  ) {
    this.customPlanForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      organisation_name: [''],
    });
    this.authenticationService.currentUser?.subscribe(x => {
        this.currentUser = x;

        const currentUserData = localStorage.getItem('currentUser');
        const userLocal = currentUserData ? JSON.parse(currentUserData) : null;
        this.activePlanId = userLocal?.plan_id || null;

        if (this.imagePacks.length || this.videoPacks.length) {
            // user logged in while staying on the page
            this.markActivatedPack();
            this.cdref.detectChanges();
        }
    });


    this.apiService.fetchSettings().subscribe(result => {
      this.setting = result.data;
            // console.log(this.setting);
      this.updateSetting();
      this.loaded = true;
    });
  }

  ngOnInit(): void {
    if (window.innerWidth <= 500) {
      this.isMobile = true;
    }
    this.getPlanDetail();
  }

  activeTab: string = 'images';
  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.applyFilters();
  }


  updateSetting() {
    this.slides = this.setting?.plan?.slides?.filter(item => item.published == 1) || [];
  }

  openSignIn() {
    this.modalService.showLoginModal();
  }


  public getPlanDetail() {
    this.apiService.getPlanDetail().subscribe(response => {
      // The endpoint answers {data: [...]}; a bare array is tolerated so the page
      // does not go blank if that ever changes back.
      const packs: CreditPack[] = Array.isArray(response) ? response : (response?.data || []);

      // status/ip_pack_status are filtered server side; repeated here so a pack
      // switched off can never reach a card through a cached or proxied response.
      const live = packs.filter(p => Number(p.status) === 1 && Number(p.ip_pack_status) === 1);

      /*
       * Subscription tiers only. The same endpoint now also carries the one-time
       * packs, which belong to the <one-time-packs> section further down the page
       * — without this they would arrive here too and be drawn as fourteen extra
       * subscription cards, at up to ₹2,00,000, in the middle of Starter/Pro.
       *
       * `pack` is what separates them, and it is a SET column: a row may
       * legitimately carry both values, so this tests membership rather than
       * comparing the whole string.
       */
      const rail = live.filter(p => isLargePack(p));

      const views = rail.map(p => this.toView(p))
        .sort((a, b) => a.price - b.price);

      this.imagePacks = views.filter(p => p.pack_type !== 'video');
      this.videoPacks = views.filter(p => p.pack_type === 'video');

      /*
       * Open on the tab that has something in it. activeTab defaults to 'images',
       * and the tab strip is now hidden whenever only one kind is on sale — so a
       * video-only catalogue would sit on an empty Images grid with no control on
       * screen to get off it.
       */
      if (!this.imagePacks.length && this.videoPacks.length) {
        this.activeTab = 'videos';
      } else if (!this.videoPacks.length) {
        this.activeTab = 'images';
      }

      if (this.activePlanId) {
        this.markActivatedPack();
      }

      this.applyFilters();
    }, err => {
      console.error('getPlanDetail error', err);
      this.imagePacks = [];
      this.videoPacks = [];
      this.applyFilters();
    });
  }

  /** The packs behind the tab that is open, before the duration filter. */
  private packsForTab(): PackView[] {
    return this.activeTab === 'videos' ? this.videoPacks : this.imagePacks;
  }

  /**
   * Rebuilds the duration pills and the grid for the current tab.
   *
   * A tab need not offer every duration, so the selection falls back to the
   * shortest one available rather than leaving the grid empty.
   */
  private applyFilters() {
    const packs = this.packsForTab();

    this.durations = DURATION_ORDER.filter(d => packs.some(p => p.duration === d));

    // Anything the backend sends that is not one of the four known durations
    // still deserves a pill, otherwise its packs would be unreachable.
    packs.forEach(p => {
      if (p.duration && this.durations.indexOf(p.duration) === -1) {
        this.durations.push(p.duration);
      }
    });

    if (this.durations.indexOf(this.selectedDuration) === -1) {
      this.selectedDuration = this.durations.length ? this.durations[0] : '';
    }

    this.visiblePacks = this.selectedDuration
      ? packs.filter(p => p.duration === this.selectedDuration)
      : packs;

    /*
     * The middle card carries the badge — the pack cards are sorted by price, and
     * the tier being pushed is the one between the entry and the top, never the
     * dearest. Left of centre when there is an even number, and no badge at all
     * below three cards, where "most popular" of two reads as arbitrary.
     */
    this.popularPackId = this.visiblePacks.length >= 3
      ? this.visiblePacks[Math.floor((this.visiblePacks.length - 1) / 2)].id
      : null;

    this.buildCompareColumns();

    this.cdref.detectChanges();
    this.refreshPackScroll();
  }

  /**
   * The compare table's plan columns, from whatever is visible.
   *
   * Follows the term control rather than pinning to monthly: the counts below are
   * a term's worth of credits, so showing them beside monthly prices while the
   * customer is looking at yearly would be comparing two different things.
   */
  private buildCompareColumns(): void {
    const packs = this.visiblePacks;

    this.compareColumns = packs.map((p, i) => ({
      name: p.pack_name,
      price: p.price,
      credits: Number(p.total_credits) || 0,
      // Same rule the cards use: the middle one, never the dearest, and no badge
      // below three columns where "most popular" of two reads as arbitrary.
      featured: packs.length >= 3 && i === Math.floor((packs.length - 1) / 2)
    }));

    if (this.compareColumns.length
        && !this.compareColumns.some(c => c.name === this.comparePick)) {
      const featured = this.compareColumns.filter(c => c.featured)[0];
      this.comparePick = (featured || this.compareColumns[0]).name;
    }
  }

  /**
   * One cell of the credit-usage panel.
   *
   * A dash, not "0 credits", where an action has no price on that medium: zero
   * reads as free, which is the opposite of not offered.
   */
  public creditLabel(credits: number): string {
    const n = Number(credits) || 0;

    return n ? n + (n === 1 ? ' credit' : ' credits') : '—';
  }

  /** How many runs of one tool a column's credits buy. */
  public compareRuns(credits: number, per: number): number {
    return Math.floor((Number(credits) || 0) / Math.max(1, per));
  }

  /**
   * Moves the rail one card along.
   *
   * A card's width plus the gap, measured off the rail rather than written down:
   * the card is a third of the track on desktop and most of it on a phone, so a
   * fixed step would overshoot on one and undershoot on the other.
   */
  public scrollPacks(direction: number): void {
    const track = this.packTrack && this.packTrack.nativeElement;
    if (!track) {
      return;
    }

    const card = track.querySelector('.np-rail__item') as HTMLElement;
    const gap = card && card.nextElementSibling
      ? (card.nextElementSibling as HTMLElement).offsetLeft - card.offsetLeft - card.offsetWidth
      : 0;
    const step = card ? card.offsetWidth + gap : track.clientWidth;

    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  /**
   * Keeps the arrows in step with where the rail actually is.
   *
   * Bound to the window as well as the rail: a resize changes how many cards fit
   * without the rail scrolling, and the arrows would otherwise keep answering
   * for the old width.
   */
  @HostListener('window:resize')
  public onPackScroll(): void {
    const track = this.packTrack && this.packTrack.nativeElement;

    if (!track) {
      this.canScrollPacksLeft = false;
      this.canScrollPacksRight = false;
      return;
    }

    // A pixel of slack: scrollLeft can land fractionally short of the end on a
    // zoomed or high-DPI display, which would leave the arrow enabled forever.
    const max = track.scrollWidth - track.clientWidth;
    this.canScrollPacksLeft = track.scrollLeft > 1;
    this.canScrollPacksRight = track.scrollLeft < max - 1;
  }

  /**
   * Re-reads the rail after the cards have been laid out.
   *
   * A frame late on purpose: applyFilters() renders the cards, and measuring in
   * the same tick would measure the row they are replacing.
   */
  private refreshPackScroll(): void {
    setTimeout(() => {
      this.onPackScroll();
      this.cdref.detectChanges();
    });
  }

  /**
   * Takes the Custom card's CTA to the enquiry form further down the page.
   *
   * A scroll rather than a link: the form it lands on is on this same page, and
   * routing away to /contact would lose the plan the reader was looking at.
   */
  public scrollToCustomPlan(): void {
    const section = document.getElementById('custom-plan');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public setDuration(duration: string) {
    if (this.selectedDuration === duration) {
      return;
    }
    this.selectedDuration = duration;
    this.applyFilters();
  }

  /**
   * What committing to a longer term saves, as a whole percent — the "Save 5%"
   * line under Quarterly and Yearly.
   *
   * Derived from the packs' own credit_value_inr rather than written down, so it
   * cannot drift from what the cards actually charge. Monthly is the baseline and
   * shows nothing.
   *
   * Compared per PACK NAME, not across the whole tab: Starter, Pro and Business
   * each have their own ₹/credit, and comparing the cheapest monthly rate against
   * the cheapest yearly one would be comparing two different products.
   *
   * Returns null when the tiers disagree. A single "Save 5%" is only honest if it
   * is true of every plan behind it — where it is not, saying nothing beats
   * quoting a number that holds for one tier and not the next.
   */
  public durationSaving(duration: string): number | null {
    if (!duration || duration === 'monthly') { return null; }

    const packs = this.packsForTab();
    const rateOf = (name: string, term: string): number => {
      const match = packs.find(p => p.pack_name === name && p.duration === term);
      return match ? Number(match.credit_value_inr) || 0 : 0;
    };

    const names = packs.filter(p => p.duration === duration).map(p => p.pack_name);
    let agreed: number | null = null;

    for (const name of names) {
      const monthly = rateOf(name, 'monthly');
      const here = rateOf(name, duration);

      // No monthly counterpart, or no discount — nothing to claim for this tier.
      if (!monthly || !here || here >= monthly) { return null; }

      const saving = Math.round(((monthly - here) / monthly) * 100);

      if (agreed === null) { agreed = saving; }
      else if (agreed !== saving) { return null; }
    }

    return agreed && agreed > 0 ? agreed : null;
  }

  public durationLabel(duration: string): string {
    return DURATIONS[duration] ? DURATIONS[duration].label : this.titleCase(duration);
  }

  /**
   * A credit pack activation stores its template id in the customer plan's
   * plan_id, so the id cached at login is the pack the customer is already on.
   */
  markActivatedPack() {
    const mark = (packs: PackView[]) => packs.forEach(p => {
      p.isActivated = (p.id === Number(this.activePlanId));
    });

    mark(this.imagePacks);
    mark(this.videoPacks);
  }

  /** Everything a card shows, worked out from one API pack. */
  private toView(pack: CreditPack): PackView {
    const credits = Number(pack.total_credits) || 0;
    const rate = Number(pack.credit_value_inr) || 0;
    const meta = DURATIONS[pack.duration];
    const groups = this.buildGroups(pack.details || []);

    /*
     * The card's words — description, INCLUDES heading and bullets, Good to know
     * — are the pricing dialog's, from the shared plan-copy, so the two surfaces
     * describe a plan identically. A pack with no written copy (Essential) gets
     * the shared default there too, rather than a list counted off its tool rows.
     *
     * Credits are not a bullet: the price block above already states them, built
     * from the pack, as the dialog's card does.
     */
    const copy = tierCopy(pack.pack_name);

    /*
     * Price and grant by entitlement — credits only, images only, or both — from
     * the server's figures (price_inr / grant_*), which are what checkout charges
     * and what the plan will hold. Credits × rate is the fallback for a response
     * without them, and is what a credits pack costs anyway.
     */
    const grantCredits = pack.grant_credits !== undefined ? Number(pack.grant_credits) || 0 : credits;
    const grantImages = Number(pack.grant_images) || 0;
    const price = pack.price_inr !== undefined ? Number(pack.price_inr) || 0 : credits * rate;
    const grants: string[] = [];
    if (grantCredits > 0) { grants.push(grantCredits.toLocaleString() + ' credits'); }
    if (grantImages > 0) { grants.push(grantImages.toLocaleString() + ' images'); }
    const months = meta ? meta.months : 0;

    return {
      ...pack,
      price,
      // Only worth showing where it is not simply the price again.
      /*
       * The card leads with the monthly figure and puts the real charge on the
       * line below, whichever term is selected.
       *
       * Every tier is then quoted in the same unit, so the cards can be read
       * across; leading with the term total made a quarterly plan look three
       * times a monthly one at a glance. The pricing dialog already reads this
       * way — this brings the page into line with it.
       *
       * A duration the backend sends that is not one of the four known ones has
       * no month count to divide by, so it keeps quoting its own period and
       * shows no second figure rather than inventing one.
       */
      perMonth: months > 1 ? Math.round(price / months) : price,
      headlinePeriod: months ? 'month' : (pack.duration || 'term'),
      termTotal: months > 1 ? price : null,
      lead: copy.lead,
      includesLead: copy.includesLead,
      features: copy.includes,
      info: copy.notes,
      grantLine: grants.join(' + '),
      groups,
      toolCount: this.toolCountOf(pack),
      isActivated: this.activePlanId ? pack.id === Number(this.activePlanId) : false,
    };
  }

  private toolCountOf(pack: CreditPack): number {
    return (pack.details || []).length;
  }

  /** Buckets the tool lines by group_key, known groups first, in a fixed order. */
  private buildGroups(tools: CreditPackTool[]): PackToolGroup[] {
    const buckets: { [key: string]: CreditPackTool[] } = {};

    tools.forEach(tool => {
      // group_key is written by the backend, but fall back to the service_code
      // prefix so a line saved before that column existed still lands somewhere.
      const key = tool.group_key || (tool.service_code || 'other').split('.')[0];
      (buckets[key] = buckets[key] || []).push(tool);
    });

    const keys = Object.keys(buckets).sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a);
      const bi = GROUP_ORDER.indexOf(b);
      // Unknown groups keep their own alphabetical order after the known ones.
      if (ai === -1 && bi === -1) { return a < b ? -1 : 1; }
      if (ai === -1) { return 1; }
      if (bi === -1) { return -1; }
      return ai - bi;
    });

    return keys.map(key => {
      const meta = TOOL_GROUPS[key];
      const items = buckets[key];
      const title = meta ? meta.title : this.titleCase(key.replace(/_/g, ' '));

      let feature: string;
      if (meta && meta.fixed) {
        feature = meta.fixed;
      } else if (meta) {
        feature = items.length + ' ' + (items.length === 1 ? meta.one : meta.many);
      } else {
        feature = items.length + ' ' + title.toLowerCase();
      }

      return { key, title, feature, tools: items };
    });
  }

  private titleCase(value: string): string {
    const text = String(value || '').replace(/-/g, ' ');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  }

  openCheckout(id: any) {
    //  console.log(this.currentUser);
    if (!id) return; // safety
    if (this.currentUser) {
      this.router.navigate(['/shop/checkout', id], {});
    } else {
      this.openSignIn();
    }
  }

  toggleFaq(index: number) {
    this.faqOpen = this.faqOpen === index ? null : index;
  }

  submitCustomPlanEnquiry() {
    if (this.customPlanForm.invalid) {
      this.customPlanForm.markAllAsTouched();
      return;
    }
    this.enquirySubmitting = true;
    this.enquiryError = '';
    this.enquirySuccess = false;

    this.apiService.submitCustomPlanEnquiry(this.customPlanForm.value).subscribe(
      () => {
        this.enquirySubmitting = false;
        this.enquirySuccess = true;
        this.toastrService.success('Thank you! Your enquiry has been submitted. We\'ll get in touch soon.');
        this.customPlanForm.reset({ customer_type: 'individual' });
        this.cdref.detectChanges();
      },
      (err) => {
        this.enquirySubmitting = false;
        this.toastrService.error('Something went wrong, please try again!');

        this.enquiryError = err?.error?.message || 'Something went wrong. Please try again.';
        this.cdref.detectChanges();
      }
    );
  }
}
